// src/components/Auth/RequireAuth.tsx
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { useUserContext } from '../Context/UserContext';

function buildNext(loc: ReturnType<typeof useLocation>) {
  return loc.pathname + (loc.search || '') + (loc.hash || '');
}
function setIntentFromPath(pathname: string) {
  try {
    const coll = /^\/coll\/([^/?#]+)/i.exec(pathname);
    const sess = /^\/session\/([^/?#]+)/i.exec(pathname);
    if (coll) {
      localStorage.setItem('registerSource', 'collaborator');
      localStorage.setItem('pendingSessionId', decodeURIComponent(coll[1]));
    } else if (sess) {
      localStorage.setItem('registerSource', 'initiator');
      localStorage.setItem('pendingSessionId', decodeURIComponent(sess[1]));
    }
  } catch {}
}

export default function RequireAuth() {
  const { isAuthenticated, email } = useUserContext();
  const location = useLocation();

  const lsEmail = useMemo(() => {
    try { const v = localStorage.getItem('email') || ''; return v.includes('@') ? v : ''; }
    catch { return ''; }
  }, []);

  const authedNow = !!(isAuthenticated || lsEmail || (email && email.includes('@')));
  const [checked, setChecked] = useState(authedNow);
  const [authed, setAuthed] = useState(authedNow);

  // One-shot silent check on first paint if we don't know yet
  useEffect(() => {
    if (authedNow) { setChecked(true); setAuthed(true); return; }
    let alive = true;
    (async () => {
      try {
        await getCurrentUser(); // throws if not signed in
        const attrs = await fetchUserAttributes().catch(() => ({} as any));
        const em = (attrs as any)?.email || (attrs as any)?.preferred_username || '';
        if (em && em.includes('@')) {
          try { localStorage.setItem('email', em); } catch {}
          if (alive) { setAuthed(true); setChecked(true); }
          return;
        }
      } catch { /* not signed in */ }
      if (alive) { setAuthed(false); setChecked(true); }
    })();
    return () => { alive = false; };
  }, [authedNow]);

  if (!checked) {
    // small placeholder while we decide (prevents bouncing to /secure-access)
    return <div style={{ padding: 24, textAlign: 'center' }}>Checking your session…</div>;
  }

  if (!authed) {
    const next = buildNext(location);
    try { localStorage.setItem('nextAfterAuth', next); setIntentFromPath(location.pathname); } catch {}
    return <Navigate to={`/secure-access?next=${encodeURIComponent(next)}`} replace />;
  }

  return <Outlet />;
}
